import { applyTo as pass, pipe, prop, values } from "ramda"
import * as Caritat from "caritat"
import { equals, expect, test } from "@benchristel/taste"
import {
  PBallotBox,
  Candidate,
  emptyPoll,
  phenom,
  PollCRDT,
  withBallot,
  withCandidate,
  withCandidateDeleted,
} from "./poll"

export type Ranking = string[]
export function schulzeMethod(
  ballots: PBallotBox,
  candidates: Candidate[],
): Ranking {
  const election = makeCaritatElection(ballots, candidates)
  const bordaRanking = Caritat.borda(election)
  return Caritat.condorcet.schulze(election, {
    seats: candidates.length,
    tiebreak: ([a, b]) =>
      bordaRanking.indexOf(a) < bordaRanking.indexOf(b) ? b : a,
  })
}

test("borda", {
  "works"() {
    const candidates = ["Honda", "Toyota", "Mitsubishi"]
    const ballots = [["Honda", "Toyota", "Mitsubishi"]].map(
      (ballot: string[]) => new Caritat.Ballot(ballot),
    )

    expect(
      Caritat.borda(new Caritat.Election({ ballots, candidates })),
      equals,
      ["Honda", "Toyota", "Mitsubishi"],
    )
  },

  "ranks A above B if all voters prefer A to B, even if there are ties"() {
    const candidates = ["cabbage", "kimchi", "buttermilk"]
    const ballots = [
      ["kimchi", "cabbage", "buttermilk"],
      ["buttermilk", "kimchi", "cabbage"],
    ].map((ballot: string[]) => new Caritat.Ballot(ballot))

    expect(
      Caritat.borda(new Caritat.Election({ ballots, candidates })),
      equals,
      ["kimchi", "buttermilk", "cabbage"],
    )
  },
})

test("phenom -> caritat integration", {
  "allows an empty poll"() {
    const poll = phenom({ ...emptyPoll(), id: "foo" })
    expect(schulzeMethod(poll.ballots, poll.candidates), equals, [])
  },
  "allows a poll with candidates but no ballots"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "foo", content: "Edgy" }),
        withCandidate({ id: "bar", content: "Mature" }),
        withCandidate({ id: "baz", content: "Youthful" }),
        withCandidate({ id: "qux", content: "Fancy" }),
      ),
    )
    const phenomenon = phenom({ ...poll, id: "foo" })
    expect(schulzeMethod(phenomenon.ballots, phenomenon.candidates), equals, [
      "foo",
      "bar",
      "baz",
      "qux",
    ])
  },
  "allows a poll with a deleted candidates"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "foo", content: "Edgy" }),
        withCandidate({ id: "bar", content: "Mature" }),
        withCandidate({ id: "baz", content: "Youthful" }),
        withCandidate({ id: "qux", content: "Fancy" }),
        withCandidateDeleted("foo"),
      ),
    )
    const phenomenon = phenom({ ...poll, id: "foo" })
    expect(schulzeMethod(phenomenon.ballots, phenomenon.candidates), equals, [
      "bar",
      "baz",
      "qux",
    ])
  },
  "allows a poll with a deleted candidate and a ballot with that candidate"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "foo", content: "Edgy" }),
        withCandidateDeleted("foo"),
        withBallot("007", ["foo"]),
      ),
    )
    const phenomenon = phenom({ ...poll, id: "foo" })
    expect(schulzeMethod(phenomenon.ballots, phenomenon.candidates), equals, [])
  },
  "allows a poll with radically out-of-touch ballot"() {
    const poll: PollCRDT = withBallot("007", ["foo"], emptyPoll())

    const phenomenon = phenom({ ...poll, id: "foo" })
    expect(schulzeMethod(phenomenon.ballots, phenomenon.candidates), equals, [])
  },
})

function makeCaritatElection(
  ballots: PBallotBox,
  candidates: Candidate[],
): Caritat.Election {
  return new Caritat.Election({
    ballots: values(ballots).map(
      (ranking) => new Caritat.Ballot(ranking.map(prop("id"))),
    ),
    candidates: candidates.map(prop("id")),
  })
}
test("caritatElection", {
  "includes all candidates"() {
    const ballots: PBallotBox = {
      foo: [
        { content: "", id: "a" },
        { content: "", id: "b" },
      ],
      bar: [{ content: "", id: "c" }],
    }

    expect(
      makeCaritatElection(ballots, [
        { content: "milk", id: "a" },
        { content: "milk", id: "b" },
        { content: "milk", id: "c" },
      ]).candidates,
      equals,
      ["a", "b", "c"],
    )
    expect(
      makeCaritatElection(ballots, [
        { content: "milk", id: "a" },
        { content: "milk", id: "b" },
        { content: "milk", id: "c" },
      ]).ballots[0]?.votes,
      equals,
      ["a", "b"],
    )
    expect(true, equals, true)
  },
  "handles deleted candidates"() {},
})
